import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";

import useTodayCashbox from "../hooks/useTodayCashbox";
import CashboxTransactionsTable from "../table/CashboxTransactionsTable";
import { formatMoney } from "helpers/numberHelper";
import CustomAlert from "components/Common/ui/CustomAlert";
import DetailCashboxTransactionModal from "../modals/DetailCashboxTransactionModal";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import { FilterField } from "components/Common/TableFilter/TableFilter";
import StatCard from "components/Common/StatCard";

const CashboxTodayPage: React.FC = () => {
    const { t } = useTranslation();
    const ability = useAbility();

    const {
        transactions,
        summary,
        hasTodayCashBox,
        loading,
        fetchTodayCashbox,
        pagination
    } = useTodayCashbox();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
    const [filters, setFilters] = useState<{ [key: string]: any }>({});
    const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);

    const handleViewDetails = (transaction: any) => {
        setSelectedTransaction(transaction);
        setIsModalDetailOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalDetailOpen(false);
        setSelectedTransaction(null);
    };

    useEffect(() => {
        fetchTodayCashbox({ page: 1, limit: 10 });
    }, [fetchTodayCashbox]);

    const breadcrumbItems = [
        { label: "finance_dashboard", path: all_routes.financeDashboard },
        { label: t("cashboxTransactions.title"), active: true },
    ];

    const filterFields: FilterField[] = [
        {
            key: "type",
            label: t("cashboxTransactions.fields.direction"),
            type: "select",
            options: [
                { label: t("cashboxTransactions.type.in"), value: "in" },
                { label: t("cashboxTransactions.type.out"), value: "out" },
            ],
        },
        { key: "date", label: t("cashboxTransactions.fields.date"), type: "range", rangeKeys: { start: "startDate", end: "endDate" } },
    ];

    const handleFilter = (newFilters: any) => {
        const params = {
            ...newFilters,
            page: 1,
            limit: 10,
        };

        setFilters(newFilters);
        fetchTodayCashbox(params);
    };

    const handleTableChange = (pg: any) => {
        const params = {
            ...filters,
            page: pg.current || 1,
            limit: pg.pageSize || 10,
        };

        fetchTodayCashbox(params);
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader
                    title={t("cashboxTransactions.title")}
                    breadcrumbItems={breadcrumbItems}
                />

                {!hasTodayCashBox && !loading && (
                    <CustomAlert
                        message={t("alert.no_cashbox_for_today.message")}
                        description={t("alert.no_cashbox_for_today.description")}
                        showIcon
                    />
                )}

                {hasTodayCashBox && (
                    <>
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <StatCard
                                    color="blue"
                                    icon="wallet"
                                    title={t("cashboxTransactions.summary.initial_balance")}
                                    value={formatMoney(summary.initialBalance)}
                                />
                            </div>
                            <div className="col-md-4">
                                <StatCard
                                    color="green"
                                    icon="currency-dollar"
                                    title={t("cashboxTransactions.summary.current_balance")}
                                    value={formatMoney(summary.currentBalance)}
                                />
                            </div>
                            <div className="col-md-4">
                                <StatCard
                                    color={summary.difference >= 0 ? "green" : "red"}
                                    icon={summary.difference >= 0 ? "trending-up" : "trending-down"}
                                    title={t("cashboxTransactions.summary.difference")}
                                    value={formatMoney(summary.difference)}
                                />
                            </div>
                        </div>

                        <div className="d-flex mb-3 justify-content-end">
                            <TableFilterDynamique
                                fields={filterFields}
                                filters={filters}
                                onApply={handleFilter}
                                onReset={() => {
                                    setFilters({});
                                    fetchTodayCashbox({ page: 1, limit: 10 });
                                }}
                            />
                        </div>

                        <CashboxTransactionsTable
                            data={transactions}
                            loading={loading}
                            handleViewDetails={handleViewDetails}
                            dataPaginate={{
                                current: pagination?.page || 1,
                                pageSize: pagination?.limit || 10,
                                total: pagination?.total,
                            }}
                            onChange={handleTableChange}
                        />
                    </>
                )}

                <DetailCashboxTransactionModal
                    isOpen={isModalDetailOpen}
                    onClose={handleCloseModal}
                    transaction={selectedTransaction}
                />
            </div>
        </div>
    );
};

export default CashboxTodayPage;